import { useState, useMemo } from "react";
import { scoreTown } from "../lib/scoring.js";
import { scoreListing, colorForListingScore } from "../lib/listingScoring.js";
import { loc, prettyConcelho, formatEUR, formatArea } from "../lib/localization.js";
import { bucketForAgingIndex } from "../lib/demographics.js";

import { ScoreExplainer } from "./ScoreExplainer.jsx";
const FILTERS = [
  { id: "all",     label: "Alle" },
  { id: "passing", label: "Passend" },
  { id: "top5",    label: "Top 5" },
];

function metricChip(icon, value, label, color) {
  return (
    <span className="rk-metric" title={label}>
      <span className="rk-metric-ico">{icon}</span>
      <span className="rk-metric-val" style={color ? { color } : undefined}>{value}</span>
    </span>
  );
}

function ListingRow({ l, towns, concelhoBySlug, weights, enforceVeto, onSelectListing }) {
  const { score, veto } = scoreListing(l, towns, concelhoBySlug, weights, enforceVeto);
  const isVeto   = veto.length > 0;
  const priceStr = l.price_eur     ? formatEUR(l.price_eur)       : "—";
  const areaStr  = l.area_total_m2 ? formatArea(l.area_total_m2)  : null;
  const bedStr   = l.bedrooms      ? `T${l.bedrooms}`             : null;
  const meta     = [areaStr, bedStr].filter(Boolean).join(" · ");
  return (
    <li
      className={`rk-listing-row`}
      onClick={e => { e.stopPropagation(); onSelectListing(l); }}
      role="button" tabIndex={0}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.stopPropagation(), onSelectListing(l))}
    >
      <span className="rk-listing-score" style={{ background: colorForListingScore(score, veto) }}>
        {isVeto ? "✗" : score}
      </span>
      <span className="rk-listing-info">
        <span className="rk-listing-price">{priceStr}</span>
        {meta && <span className="rk-listing-meta">{meta}</span>}
      </span>
    </li>
  );
}

function TownListings({ listings, towns, concelhoBySlug, weights, enforceVeto, onSelectListing }) {
  const [open, setOpen] = useState(false);
  if (!listings || listings.length === 0) return null;
  return (
    <div className="rk-listings">
      <button
        className="rk-listings-toggle"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        type="button"
      >
        <span className="rk-listings-ico">🏠</span>
        <span className="rk-listings-label">{listings.length} {listings.length === 1 ? "huis" : "huizen"}</span>
        <span className="rk-listings-chevron">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <ul className="rk-listings-list">
          {listings.map(l => (
            <ListingRow
              key={l.url} l={l}
              towns={towns} concelhoBySlug={concelhoBySlug}
              weights={weights} enforceVeto={enforceVeto}
              onSelectListing={onSelectListing}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export function Ranking({ towns, concelhoBySlug, listings, weights, enforceVeto, onSelectTown, onSelectListing, isOpen,
  compareList = [],
  onToggleCompare,
  waterVeto = false,
}) {
  const [filter, setFilter] = useState("passing");

  const scored = useMemo(() => {
    const s = towns.map(t => {
      const c  = concelhoBySlug[t.concelho];
      const sc = scoreTown(t, weights, c, enforceVeto, { waterVeto });
      return { t, c, ...sc, fit: c?.fit_score };
    });
    s.sort((a, b) => {
      if (b.pct !== a.pct) return b.pct - a.pct;
      const na = (a.t?.name || a.t?.concelho || "").toLowerCase();
      const nb = (b.t?.name || b.t?.concelho || "").toLowerCase();
      return na.localeCompare(nb, "pt");
    });
    return s;
  }, [towns, concelhoBySlug, weights, enforceVeto, waterVeto]);

  const listingsByConcelhoSlug = useMemo(() => {
    const map = {};
    for (const l of (listings || [])) {
      const key = l.concelho || "";
      if (!map[key]) map[key] = [];
      map[key].push(l);
    }
    return map;
  }, [listings]);

  const filtered = useMemo(() => {
    if (filter === "passing") return scored.filter(x => x.pct >= 55);
    if (filter === "top5")    return scored.slice(0, 5);
    return scored;
  }, [scored, filter]);

  const passingCount = scored.filter(x => x.pct >= 55).length;

  return (
    <aside className={`sidebar sidebar-right ranking-v2 ${isOpen ? "open" : ""}`} aria-label="Ranglijst">
      <div className="rk-head">
        <h2 className="rk-title">Top plekken</h2>
        <span className="rk-subtitle">{passingCount}/{scored.length} voldoen aan jullie criteria</span>
      </div>

      <div className="rk-filter-row" role="tablist">
        {FILTERS.map(f => (
          <button key={f.id} role="tab" aria-selected={filter === f.id}
            className={`rk-filter-chip ${filter === f.id ? "active" : ""}`}
            onClick={() => setFilter(f.id)} type="button"
          >
            {f.label}
            <span className="rk-filter-count">
              {f.id === "all"     ? scored.length :
               f.id === "passing" ? passingCount  :
               Math.min(5, passingCount)}
            </span>
          </button>
        ))}
      </div>

      <ScoreExplainer towns={towns} weights={weights} />

      <ol className="rk-list">
        {filtered.map(({ t, c, pct, excluded, vetoes, fit }, i) => {
          const rainfall      = c?.climate?.annual_rainfall_mm;
          const sunshine      = c?.climate?.annual_sunshine_h;
          const aging         = c?.aging_index;
          const ageBucket     = bucketForAgingIndex(aging);
          const townListings  = listingsByConcelhoSlug[t.concelho] || [];
          const scoreColor    = excluded ? "#7a2e23"
            : pct >= 75 ? "#3d6e48" : pct >= 60 ? "#5a8268"
            : pct >= 45 ? "#d4a851" : "#b85c4d";

          return (
            <li key={t.name}
              className={`rk-row `}
              onClick={() => onSelectTown(t)} role="button" tabIndex={0}
              onKeyDown={e => (e.key === "Enter" || e.key === " ") && onSelectTown(t)}
            >
              <div className="rk-rank">{i + 1}</div>
              <div className="rk-info">
                <div className="rk-name-row">
                  <span className="rk-name">{t.name}</span>
                  {fit && <span className={`rk-fit-chip rk-fit-${fit}`}>{loc("fit_score", fit)}</span>}
                </div>
                <div className="rk-meta">
                  <span className="rk-concelho">{prettyConcelho(t.concelho)}</span>
                </div>
                <div className="rk-metrics">
                  {rainfall  != null && metricChip("🌧", `${rainfall} mm`, "Neerslag/jaar")}
                  {sunshine  != null && metricChip("☀",  `${sunshine} h`,  "Zon/jaar")}
                  {ageBucket && metricChip(ageBucket.dot, ageBucket.label, `Vergrijzing ${aging}`)}
                  {townListings.length > 0 && metricChip("🏠", String(townListings.length),
                    `${townListings.length} ${townListings.length === 1 ? "huis" : "huizen"} gevonden`,
                    "var(--c-brand)")}
                
                  {t.nearestAirport && metricChip(
                    "✈",
                    `${t.nearestAirport.driveMin} min`,
                    `${t.nearestAirport.name} (${t.nearestAirport.code})`,
                    undefined
                  )}
                  {c?.fire_risk_class >= 4 && metricChip(
                    "🔥",
                    `Brandrisico ${c.fire_risk_class}`,
                    "Hoog brandrisico — belangrijk voor boerderij/natuur",
                    "#c94040"
                  )}
</div>

                {onToggleCompare && (
                  <button
                    className={`cp-cmp-btn${
                      compareList.includes(t.name || t.concelho) ? " cp-in-list"
                      : compareList.length >= 3 ? " cp-full" : ""
                    }`}
                    onClick={e => { e.stopPropagation(); onToggleCompare(t.name || t.concelho); }}
                    disabled={compareList.length >= 3 && !compareList.includes(t.name || t.concelho)}
                  >
                    {compareList.includes(t.name || t.concelho)
                      ? "✓ In vergelijking"
                      : compareList.length >= 3 ? "Vergelijking vol"
                      : "+ Vergelijk"}
                  </button>
                )}
                {excluded && vetoes.length > 0 && (
                  <div className="rk-veto-reason">{vetoes[0]}</div>
                )}
                {onSelectListing && townListings.length > 0 && (
                  <TownListings
                    listings={townListings}
                    towns={towns} concelhoBySlug={concelhoBySlug}
                    weights={weights} enforceVeto={enforceVeto}
                    onSelectListing={onSelectListing}
                  />
                )}
              </div>
              <div className="rk-score" style={{ background: scoreColor }}>
                {Math.round(pct)}
              </div>
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li className="rk-empty">Geen plekken voldoen. Pas criteria aan of zet "Strikte criteria" uit.</li>
        )}
      </ol>
    </aside>
  );
}
